import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { subscribeToRules, respondToRule, deleteRule } from '@coparent/shared/src/services/ruleService';
import { RULE_CATEGORIES } from '@coparent/shared/src/utils/constants';
import RuleForm from '../components/rules/RuleForm';
import ChildDots from '../components/common/ChildDots';
import {
  HiPlus,
  HiCheck,
  HiChatAlt2,
  HiTrash,
  HiPencil,
  HiClock,
  HiShieldCheck,
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import NotificationBell from '../components/notifications/NotificationBell';

const STATUS_STYLES = {
  proposed: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Proposed' },
  agreed: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Agreed' },
  'needs-discussion': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Needs Discussion' },
};

const getCategoryLabel = (value) => {
  const found = RULE_CATEGORIES.find(c => c.value === value);
  return found?.label || value;
};

const RuleCard = ({ rule, family, currentUser, familyId, childrenMap, onEdit }) => {
  const [responding, setResponding] = useState(false);
  const proposer = family?.parentProfiles?.[rule.proposedBy];
  const canRespond = rule.status === 'proposed' && rule.proposedBy !== currentUser.uid;
  const isMyProposal = rule.proposedBy === currentUser.uid;
  const status = STATUS_STYLES[rule.status] || STATUS_STYLES.proposed;

  const handleAgree = async () => {
    setResponding(true);
    try {
      await respondToRule(familyId, rule.id, 'agreed', currentUser.uid);
      toast.success('Rule agreed!');
    } catch { toast.error('Failed to agree'); }
    setResponding(false);
  };

  const handleNeedsDiscussion = async () => {
    setResponding(true);
    try {
      await respondToRule(familyId, rule.id, 'needs-discussion', currentUser.uid);
      toast.success('Flagged for discussion');
    } catch { toast.error('Failed to update'); }
    setResponding(false);
  };

  const handleWithdraw = async () => {
    if (!window.confirm('Withdraw this rule proposal?')) return;
    setResponding(true);
    try {
      await deleteRule(familyId, rule.id);
      toast.success('Rule withdrawn');
    } catch { toast.error('Failed to withdraw'); }
    setResponding(false);
  };

  return (
    <div className={`card p-5 border-l-4 ${status.border}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Category badge */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[0.625rem] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide bg-surface-100 text-ink-500">
              {getCategoryLabel(rule.category)}
            </span>
            {rule.childIds?.length > 0 ? (
              <ChildDots childIds={rule.childIds} childrenMap={childrenMap} size="xs" />
            ) : (
              <span className="text-[0.625rem] text-ink-300 font-medium">All children</span>
            )}
          </div>

          <h3 className="font-bold text-ink-900 text-sm">{rule.title}</h3>
          {rule.description && (
            <p className="text-sm text-ink-400 mt-1">{rule.description}</p>
          )}
          <p className="text-xs text-ink-300 mt-2">
            Proposed by {proposer?.displayName || 'Unknown'}
          </p>
        </div>

        {/* Status badge */}
        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wide ${status.bg} ${status.text} flex-shrink-0`}>
          {status.label}
        </span>
      </div>

      {/* Actions for pending rules awaiting my response */}
      {canRespond && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-surface-100">
          <button
            onClick={handleAgree}
            disabled={responding}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors"
          >
            <HiCheck className="text-sm" />
            Agree
          </button>
          <button
            onClick={handleNeedsDiscussion}
            disabled={responding}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
          >
            <HiChatAlt2 className="text-sm" />
            Needs Discussion
          </button>
        </div>
      )}

      {/* Edit / Withdraw for my own rules */}
      {isMyProposal && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-surface-100">
          <button
            onClick={() => onEdit(rule)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-ink-400 hover:text-ink-700 hover:bg-surface-100 transition-colors"
          >
            <HiPencil className="text-sm" />
            Edit
          </button>
          {rule.status === 'proposed' && (
            <button
              onClick={handleWithdraw}
              disabled={responding}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-ink-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            >
              <HiTrash className="text-sm" />
              Withdraw
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const HouseholdRulesPage = () => {
  const { currentUser } = useAuth();
  const { family, familyId, childrenMap } = useFamily();
  const [rules, setRules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  useEffect(() => {
    if (!familyId) return;
    return subscribeToRules(familyId, setRules);
  }, [familyId]);

  const { proposed, agreed, discussion } = useMemo(() => {
    const proposed = rules.filter(r => r.status === 'proposed').sort((a, b) => {
      const aForMe = a.proposedBy !== currentUser.uid ? 0 : 1;
      const bForMe = b.proposedBy !== currentUser.uid ? 0 : 1;
      return aForMe - bForMe;
    });
    const agreed = rules.filter(r => r.status === 'agreed');
    const discussion = rules.filter(r => r.status === 'needs-discussion');
    return { proposed, agreed, discussion };
  }, [rules, currentUser.uid]);

  const pendingForMe = proposed.filter(r => r.proposedBy !== currentUser.uid).length;

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setShowForm(true);
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between page-header">
        <div>
          <h1 className="page-title">Household Rules</h1>
          <p className="page-subtitle">
            {pendingForMe > 0
              ? `${pendingForMe} rule${pendingForMe > 1 ? 's' : ''} waiting for your response`
              : 'Agree on consistent rules across both homes'
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setEditingRule(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <HiPlus className="text-base" />
            Propose Rule
          </button>
          <NotificationBell />
        </div>
      </div>

      {/* Pending Proposals */}
      {proposed.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
              <HiClock className="text-amber-500 text-sm" />
            </div>
            <h2 className="font-bold text-ink-900 text-[0.9375rem]">Pending Proposals</h2>
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
              {proposed.length}
            </span>
          </div>
          <div className="space-y-3">
            {proposed.map(rule => (
              <RuleCard
                key={rule.id}
                rule={rule}
                family={family}
                currentUser={currentUser}
                familyId={familyId}
                childrenMap={childrenMap}
                onEdit={handleEdit}
              />
            ))}
          </div>
        </section>
      )}

      {/* Needs Discussion */}
      {discussion.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
              <HiChatAlt2 className="text-red-500 text-sm" />
            </div>
            <h2 className="font-bold text-ink-900 text-[0.9375rem]">Needs Discussion</h2>
          </div>
          <div className="space-y-3">
            {discussion.map(rule => (
              <RuleCard
                key={rule.id}
                rule={rule}
                family={family}
                currentUser={currentUser}
                familyId={familyId}
                childrenMap={childrenMap}
                onEdit={handleEdit}
              />
            ))}
          </div>
        </section>
      )}

      {/* Agreed Rules */}
      <section>
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center">
            <HiShieldCheck className="text-green-500 text-sm" />
          </div>
          <h2 className="font-bold text-ink-900 text-[0.9375rem]">Agreed Rules</h2>
        </div>
        {agreed.length === 0 && proposed.length === 0 && discussion.length === 0 ? (
          <div className="card p-8 text-center">
            <HiShieldCheck className="text-3xl text-ink-200 mx-auto mb-2" />
            <p className="text-sm text-ink-400 font-medium">No household rules yet</p>
            <p className="text-xs text-ink-300 mt-1">Propose rules to keep things consistent across both homes</p>
          </div>
        ) : agreed.length === 0 ? (
          <div className="card p-8 text-center">
            <p className="text-sm text-ink-400 font-medium">No agreed rules yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {agreed.map(rule => (
              <RuleCard
                key={rule.id}
                rule={rule}
                family={family}
                currentUser={currentUser}
                familyId={familyId}
                childrenMap={childrenMap}
                onEdit={handleEdit}
              />
            ))}
          </div>
        )}
      </section>

      {showForm && (
        <RuleForm
          key={editingRule?.id || 'new'}
          onClose={() => { setShowForm(false); setEditingRule(null); }}
          rule={editingRule}
        />
      )}
    </div>
  );
};

export default HouseholdRulesPage;
